import React from "react";
import PropTypes from "prop-types";
import Box from '@mui/material/Box';
import { Typography, Link as MuiLink } from "@mui/material";
import { useHistory } from "react-router-dom";

export default function CardTable({ color = "light", ultimosResultados }) {
  const history = useHistory();
  const resultados = Array.isArray(ultimosResultados) ? ultimosResultados : [];

  function hasPendingAction(resultado) {
    return Boolean(resultado.is_pending || resultado.can_continue);
  }

  function getProgressClasses(score) {
    if (score >= 90) {
      return {
        track: 'bg-emerald-200',
        bar: 'bg-emerald-500'
      };
    }

    if (score >= 75) {
      return {
        track: 'bg-teal-200',
        bar: 'bg-teal-500'
      };
    }

    return {
      track: 'bg-red-200',
      bar: 'bg-red-500'
    };
  }

  function getStatusLabel(resultado) {
    if (hasPendingAction(resultado)) {
      return 'Pendiente';
    }

    if (resultado.status === 'completed') {
      return resultado.fecha || 'Completado';
    }

    return resultado.fecha || 'En progreso';
  }

  function getStatusClasses(resultado) {
    if (hasPendingAction(resultado)) {
      return 'bg-amber-100 text-amber-800';
    }

    return 'bg-green-100 text-green-800';
  }

  function getExamTitle(title) {
    const titleMap = {
      materias: 'por materias',
      enarm: 'ENARM',
      aleatorio: 'aleatorio',
    };

    const normalizedTitle = String(title || '').trim();
    const normalizedMode = normalizedTitle.toLowerCase().replace(/^simulador\s+/, '').trim();

    if (normalizedTitle.toLowerCase().startsWith('simulador ') && titleMap[normalizedMode]) {
      return `Simulador ${titleMap[normalizedMode]}`;
    }

    return titleMap[normalizedMode] || normalizedTitle || 'Examen sin título';
  }

  return (
    <>
    <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 3, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <div className="block w-full overflow-x-hidden">
          <h3 className="text-md font-semibold mb-2">Ultimos resultados</h3>
          {/* Projects table */}
          <table className="items-center w-full bg-transparent border-collapse table-fixed">
            <colgroup>
              <col style={{ width: "44%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "38%" }} />
            </colgroup>
            <tbody>
              {resultados.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                    No hay resultados recientes.
                  </td>
                </tr>
              ) : (
                resultados.map((resultado) => {
                  const score = Math.max(0, Math.min(100, Number(resultado.resultado) || 0));
                  const progressClasses = getProgressClasses(score);
                  const isPending = hasPendingAction(resultado);

                  return (
                    <tr key={resultado.exam_id || resultado.created_at}>
                      <th className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs py-4 text-left">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {getExamTitle(resultado.title)}
                        </Typography>
                      </th>
                      <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs py-4 whitespace-nowrap">
                        <div className="flex w-full justify-center">
                          <span className={`${getStatusClasses(resultado)} text-[0.6rem] font-light px-2 py-0.5 rounded`}>
                            {getStatusLabel(resultado)}
                          </span>
                        </div>
                      </td>
                      <td className="border-t-0 px-3 align-middle border-l-0 border-r-0 text-xs py-4">
                        {isPending ? (
                          <MuiLink
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => history.push(`/admin/simulador/${resultado.exam_id}`)}
                            sx={{
                              color: '#6366f1',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              lineHeight: 1.2,
                              background: 'none',
                              border: 'none',
                              p: 0,
                              cursor: resultado.exam_id ? 'pointer' : 'default',
                              opacity: resultado.exam_id ? 1 : 0.5,
                              pointerEvents: resultado.exam_id ? 'auto' : 'none',
                            }}
                            className="whitespace-nowrap"
                          >
                            Continuar examen
                          </MuiLink>
                        ) : (
                          <div className="flex items-center min-w-0">
                            <span className="mr-2 flex-shrink-0">{score}%</span>
                            <div className="relative flex-1 min-w-0">
                              <div className={`overflow-hidden h-2 text-xs flex rounded ${progressClasses.track}`}>
                                <div
                                  style={{ width: `${score}%` }}
                                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${progressClasses.bar}`}
                                ></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Box>
    </Box>
    </>
  );
}

CardTable.propTypes = {
  ultimosResultados: PropTypes.arrayOf(
    PropTypes.shape({
      fecha: PropTypes.string,
      title: PropTypes.string,
      status: PropTypes.string,
      exam_id: PropTypes.string,
      resultado: PropTypes.number,
      created_at: PropTypes.string,
      is_pending: PropTypes.bool,
      can_continue: PropTypes.bool,
      completed_at: PropTypes.string,
      total_answered: PropTypes.number,
      correct_answers: PropTypes.number,
    })
  ),
  color: PropTypes.oneOf(["light", "dark"]),
};
